import { useCallback, useEffect, useState } from 'react'
import { api, dropNulls } from './api'
import type { Asset, AssetCategory, AssetOptions, AssetRecords, StockAmount, StockCategory, StockDimension, StockEntryKind, StockItem, StockMovement } from './types'

// The inventory: assets (equipment and buildings), one row per physical unit, which farm logs can
// link to; stationary assets belong to one farm, movable equipment to the organization. And stock
// (pesticides, fertilizers, other inputs): each farm's own, with how much is on hand.

type Opt<T> = T | null | undefined

export const CATEGORY_LABEL: Record<AssetCategory, { plural: string; singular: string }> = {
  EQUIPMENT: { plural: 'Equipment', singular: 'equipment' },
  BUILDING: { plural: 'Buildings', singular: 'building' },
}

/** A tab of the inventory page. */
export type InventoryTab = AssetCategory | 'STOCK'

/** The farm's inventory list, on this tab (equipment is the default tab). */
export const inventoryPath = (farmId: string, tab: InventoryTab = 'EQUIPMENT') =>
  `/farms/${farmId}/inventory${tab === 'EQUIPMENT' ? '' : `?category=${tab}`}`

export type AssetInput = {
  category: AssetCategory
  /** Set = stationary at that farm; null = movable. Only on create: an asset never changes farm. */
  farmId?: Opt<string>
  name: string
  type: string
  makeModel?: Opt<string>
  description?: Opt<string>
}

/** Every page of a list endpoint (assets are few; the server caps a page at 200). */
async function allPages<T>(path: string): Promise<T[]> {
  const items: T[] = []
  let cursor: string | null = null
  do {
    const sep = path.includes('?') ? '&' : '?'
    const page: { data: T[]; nextCursor: string | null } = await api.getPage<T>(`${path}${sep}limit=200${cursor ? `&cursor=${cursor}` : ''}`)
    items.push(...page.data)
    cursor = page.nextCursor
  } while (cursor)
  return items
}

/** Active assets that can be used at this farm (its stationary ones plus every movable one), optionally of one category. */
export function useFarmAssets(orgId: string | undefined, farmId: string | undefined, category?: AssetCategory) {
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    if (!orgId || !farmId) return
    const query = new URLSearchParams({ usableAt: farmId })
    if (category) query.set('category', category)
    try {
      setAssets(await allPages<Asset>(`/orgs/${orgId}/inventory/assets?${query}`))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the inventory.')
    } finally {
      setLoading(false)
    }
  }, [orgId, farmId, category])

  useEffect(() => {
    reload()
  }, [reload])

  const base = `/orgs/${orgId}/inventory/assets`
  const addAsset = async (input: AssetInput) => {
    const asset = await api.post<Asset>(base, dropNulls(input))
    await reload()
    return asset
  }
  const updateAsset = async (id: string, { category: _category, farmId: _farmId, ...patch }: AssetInput) => {
    const asset = await api.patch<Asset>(`${base}/${id}`, patch)
    await reload()
    return asset
  }
  const archiveAsset = async (id: string) => {
    await api.delete<Asset>(`${base}/${id}`)
    await reload()
  }

  return { assets, loading, error, reload, addAsset, updateAsset, archiveAsset }
}

/** One asset by id, for its own page and its edit page. Archived assets load too (archivedAt set). */
export function useAsset(orgId: string | undefined, assetId: string | undefined) {
  const [asset, setAsset] = useState<Asset | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId || !assetId) return
    setLoading(true)
    api.get<Asset>(`/orgs/${orgId}/inventory/assets/${assetId}`).then(
      (a) => {
        setAsset(a)
        setError(null)
        setLoading(false)
      },
      (err) => {
        setAsset(null)
        setError(err instanceof Error ? err.message : 'Could not load it.')
        setLoading(false)
      },
    )
  }, [orgId, assetId])

  const path = `/orgs/${orgId}/inventory/assets/${assetId}`
  const updateAsset = async ({ category: _category, farmId: _farmId, ...patch }: AssetInput) => {
    const updated = await api.patch<Asset>(path, patch)
    setAsset(updated)
    return updated
  }
  const archiveAsset = async () => {
    setAsset(await api.delete<Asset>(path))
  }

  return { asset, loading, error, updateAsset, archiveAsset }
}

/** Creates an asset; the add page navigates to it afterwards. */
export function createAsset(orgId: string, input: AssetInput) {
  return api.post<Asset>(`/orgs/${orgId}/inventory/assets`, dropNulls(input))
}

export function useAssetOptions(orgId: string | undefined) {
  const [options, setOptions] = useState<AssetOptions | null>(null)
  useEffect(() => {
    if (!orgId) return
    api.get<AssetOptions>(`/orgs/${orgId}/inventory/asset-options`).then(setOptions, () => setOptions(null))
  }, [orgId])
  return options
}

/** The log entries linked to one asset, newest first. */
export function useAssetRecords(orgId: string | undefined, assetId: string | undefined) {
  const [records, setRecords] = useState<AssetRecords | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!orgId || !assetId) return
    setRecords(null)
    api.get<AssetRecords>(`/orgs/${orgId}/inventory/assets/${assetId}/records`).then(
      (r) => {
        setRecords(r)
        setError(null)
      },
      (err) => setError(err instanceof Error ? err.message : 'Could not load the records.'),
    )
  }, [orgId, assetId])
  return { records, error }
}

/**
 * The asset a typed name refers to: the one already linked while the name is unchanged (it may have
 * been renamed since), else an active asset with that name, case-insensitively. null = free text.
 */
export function assetIdFor(assets: Asset[], name: string, initial?: { name: string; assetId: string | null }): string | null {
  const typed = name.trim()
  if (initial?.assetId && typed === initial.name) return initial.assetId
  return assets.find((a) => a.name.toLowerCase() === typed.toLowerCase())?.id ?? null
}

// ---------------------------------------------------------------------------
// Stock
// ---------------------------------------------------------------------------

export const STOCK_CATEGORY_LABEL: Record<StockCategory, string> = { PESTICIDE: 'Pesticide', FERTILIZER: 'Fertilizer', OTHER: 'Other' }

export const STOCK_ENTRY_LABEL: Record<StockEntryKind, { title: string; verb: string }> = {
  RECEIVED: { title: 'Received', verb: 'How much came in?' },
  USED: { title: 'Used', verb: 'How much went out?' },
  COUNTED: { title: 'Count', verb: 'How much is on hand now?' },
}

export const stockPath = (farmId: string, itemId?: string) => `/farms/${farmId}/inventory/stock${itemId ? `/${itemId}` : ''}`

const stockApi = (orgId: string, farmId: string) => `/orgs/${orgId}/farms/${farmId}/inventory/stock`

/** Suggested package names (free text). Mirrors STOCK_PACKAGE_NAMES in server/src/apps/inventory/vocab.ts. */
export const STOCK_PACKAGE_NAMES = ['Jug', 'Bag', 'Tote', 'Drum', 'Pail', 'Bottle', 'Case', 'Box', 'Roll', 'Load']

export type StockItemInput = {
  category: StockCategory
  name: string
  /** Only on create: fixed once added. */
  dimension: StockDimension
  /** null follows the org's unit system. */
  displayUnit: string | null
  package: { name: string; size: number; unit: string } | null
  pesticideProductId: string | null
  /** Only on create: recorded as the first count. */
  onHand?: StockAmount
}

/** The farm's active stock items, newest first, each with on-hand. */
export function useFarmStock(orgId: string | undefined, farmId: string | undefined) {
  const [items, setItems] = useState<StockItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId || !farmId) return
    allPages<StockItem>(stockApi(orgId, farmId)).then(
      (all) => {
        setItems(all)
        setError(null)
        setLoading(false)
      },
      (err) => {
        setError(err instanceof Error ? err.message : 'Could not load the stock.')
        setLoading(false)
      },
    )
  }, [orgId, farmId])

  return { items, loading, error }
}

/** One stock item and its entries (latest first). Archived items load too (archivedAt set). */
export function useStockItem(orgId: string | undefined, farmId: string | undefined, itemId: string | undefined) {
  const [item, setItem] = useState<StockItem | null>(null)
  const [movements, setMovements] = useState<StockMovement[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId || !farmId || !itemId) return
    setLoading(true)
    const path = `${stockApi(orgId, farmId)}/${itemId}`
    Promise.all([api.get<StockItem>(path), api.get<StockMovement[]>(`${path}/movements`)]).then(
      ([i, m]) => {
        setItem(i)
        setMovements(m)
        setError(null)
        setLoading(false)
      },
      (err) => {
        setItem(null)
        setError(err instanceof Error ? err.message : 'Could not load it.')
        setLoading(false)
      },
    )
  }, [orgId, farmId, itemId])

  const path = `${stockApi(orgId ?? '', farmId ?? '')}/${itemId}`
  const updateItem = async ({ name, displayUnit, package: pkg, pesticideProductId }: StockItemInput) => {
    const updated = await api.patch<StockItem>(path, { name, displayUnit, package: pkg, pesticideProductId })
    setItem(updated)
    return updated
  }
  const archiveItem = async () => {
    setItem(await api.delete<StockItem>(path))
  }

  return { item, movements, loading, error, updateItem, archiveItem }
}

/** Adds a stock item; the add page navigates to it afterwards. */
export function createStockItem(orgId: string, farmId: string, input: StockItemInput) {
  return api.post<StockItem>(stockApi(orgId, farmId), dropNulls(input))
}

export type StockEntryInput = { kind: StockEntryKind; quantity: StockAmount; occurredOn: string; note: string | null }

export function recordStockEntry(orgId: string, farmId: string, itemId: string, input: StockEntryInput) {
  return api.post<StockMovement>(`${stockApi(orgId, farmId)}/${itemId}/movements`, dropNulls(input))
}
